/**
 * System Settings — síť, DNS, NTP, systémové info, resource registry
 * Všechna volání přes MCP tools (system-settings.*)
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../../lib/mcp';
import { toast } from 'sonner';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { StatusDot } from '../../components/ui/StatusDot';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Toggle } from '../../components/ui/Toggle';
import { EmptyState } from '../../components/ui/EmptyState';
import { AclPanel, ServicesPanel } from './acm-self/panels';
import { DataTable } from '../../components/ui/DataTable';
import { RefreshButton } from '../../components/ui/RefreshButton';
import {
  Archive,
  Clock,
  Cpu,
  Database,
  Gauge,
  Globe,
  Globe2,
  HardDrive,
  Loader2,
  Network,
  Power,
  Save,
  Server,
  Settings,
  Shield,
  Trash2,
} from 'lucide-react';
import { themedConfirm } from '../../lib/themed-dialogs';

// ── Server Environment (Prod/Beta/Dev) ──────────────────────────────────────

function ServerEnvCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['server-env'],
    queryFn: () =>
      mcpCall<{ env?: 'production' | 'staging' | 'dev' }>(
        'toolbox.sysupdate_server_env',
        { action: 'get' },
      ),
    staleTime: 30_000,
  });
  const current = (data?.env as 'production' | 'staging' | 'dev' | undefined) ?? 'dev';

  const setMut = useMutation({
    mutationFn: (env: 'production' | 'staging' | 'dev') =>
      mcpCall('toolbox.sysupdate_server_env', { action: 'set', env }),
    onSuccess: () => {
      toast.success(t('systemSettings.envChanged'));
      qc.invalidateQueries({ queryKey: ['server-env'] });
      qc.invalidateQueries({ queryKey: ['server-info'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const opts: Array<{
    key: 'production' | 'staging' | 'dev';
    label: string;
    desc: string;
    cls: string;
  }> = [
    {
      key: 'production',
      label: t('systemSettings.envProd'),
      desc: t('systemSettings.envProdDesc'),
      cls: 'bg-emerald-600 text-white',
    },
    {
      key: 'staging',
      label: t('systemSettings.envBeta'),
      desc: t('systemSettings.envBetaDesc'),
      cls: 'bg-amber-600 text-white',
    },
    {
      key: 'dev',
      label: t('systemSettings.envDev'),
      desc: t('systemSettings.envDevDesc'),
      cls: 'bg-rose-600 text-white',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Shield}>{t('systemSettings.serverEnvironment')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3 text-sm'>
        <p className='text-gray-500 text-xs'>
          {t('systemSettings.serverEnvironmentHint')}
        </p>
        <div className='flex gap-2'>
          {opts.map((o) => {
            const active = current === o.key;
            return (
              <button
                key={o.key}
                disabled={isLoading || setMut.isPending}
                onClick={async () => {
                  if (active) return;
                  if (
                    await themedConfirm(t('systemSettings.confirmSwitchEnv', { label: o.label }))
                  ) {
                    setMut.mutate(o.key);
                  }
                }}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                  active
                    ? `${o.cls} border-transparent`
                    : 'bg-gray-900 text-gray-400 border-gray-800 hover:bg-gray-800'
                }`}
              >
                <div>{o.label}</div>
                <div
                  className={`text-[10px] mt-0.5 font-normal ${
                    active ? 'text-white/80' : 'text-gray-600'
                  }`}
                >
                  {o.desc}
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(b: number): string {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
}

function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ── System Info Tab ─────────────────────────────────────────────────────────

function InfoTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['sys-info'],
    queryFn: () => mcpCall('system-settings.system_info'),
    staleTime: 30_000,
  });

  const info = data as {
    hostname?: string;
    os?: string;
    kernel?: string;
    cpu?: Record<string, unknown>;
    memory?: Record<string, number>;
    uptime?: number;
    disk?: Record<string, number>;
  } | undefined;

  return (
    <div className='space-y-4'>
      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : (
          <>
            <StatsGrid columns={4}>
              <StatCard label={t('systemSettings.hostname')} value={info?.hostname ?? '—'} icon={Server} />
              <StatCard label={t('systemSettings.os')} value={info?.os ?? '—'} icon={HardDrive} />
              <StatCard
                label={t('systemSettings.cpu')}
                value={t('systemSettings.coresValue', { cores: info?.cpu?.cores ?? '?' })}
                icon={Cpu}
              />
              <StatCard
                label={t('systemSettings.uptime')}
                value={formatUptime(Number(info?.uptime) || 0)}
                icon={Clock}
              />
            </StatsGrid>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <Card>
                <CardHeader>
                  <CardTitle icon={Cpu}>{t('systemSettings.cpu')}</CardTitle>
                </CardHeader>
                <CardContent className='space-y-2 text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>{t('systemSettings.model')}</span>
                    <span className='text-white'>{String(info?.cpu?.model ?? '—')}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>{t('systemSettings.cores')}</span>
                    <span className='text-white'>{String(info?.cpu?.cores ?? '—')}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>{t('systemSettings.load')}</span>
                    <span className='text-white'>{Number(info?.cpu?.load ?? 0).toFixed(1)}%</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle icon={Database}>{t('systemSettings.ram')}</CardTitle>
                </CardHeader>
                <CardContent className='space-y-2 text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>{t('systemSettings.total')}</span>
                    <span className='text-white'>{formatBytes(info?.memory?.total ?? 0)}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>{t('systemSettings.used')}</span>
                    <span className='text-white'>{formatBytes(info?.memory?.used ?? 0)}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-500'>{t('systemSettings.free')}</span>
                    <span className='text-white'>{formatBytes(info?.memory?.free ?? 0)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <ServerEnvCard />
          </>
        )}
    </div>
  );
}

// ── Network Tab ─────────────────────────────────────────────────────────────

function NetworkTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['sys-networks'],
    queryFn: () => mcpCall('system-settings.system_network_list'),
  });

  const networks = (data as { networks?: Array<Record<string, unknown>> })?.networks ?? [];

  return (
    <div className='space-y-4'>
      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : networks.length === 0
        ? (
          <EmptyState
            icon={<Network size={24} />}
            title={t('systemSettings.noInterfaces')}
            description={t('systemSettings.noInterfacesDesc')}
          />
        )
        : (
          <div className='space-y-2'>
            {networks.map((n, i) => (
              <Card key={i}>
                <CardContent className='py-3'>
                  <div className='flex items-center gap-3'>
                    <StatusDot status={n.state === 'up' ? 'running' : 'error'} />
                    <span className='font-mono text-sm text-white flex-1'>{String(n.name)}</span>
                    <span className='text-xs text-gray-400 font-mono'>
                      {String(n.ip_address ?? '—')}/{String(n.netmask ?? '—')}
                    </span>
                    <Badge>{String(n.type ?? '?')}</Badge>
                  </div>
                  <div className='flex gap-4 mt-2 ml-6 text-[10px] text-gray-600'>
                    <span>MAC: {String(n.mac_address ?? '—')}</span>
                    <span>GW: {String(n.gateway ?? '—')}</span>
                    <span>DNS: {(n.dns as string[] ?? []).join(', ') || '—'}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

// ── DNS Tab ─────────────────────────────────────────────────────────────────

function DnsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['sys-dns'],
    queryFn: () => mcpCall('system-settings.system_dns', { action: 'get' }),
  });

  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');
  const [domains, setDomains] = useState('');

  const dns = data as
    | { primary_dns?: string; secondary_dns?: string; search_domains?: string[] }
    | undefined;

  // Sync state from API
  if (dns && !primary && dns.primary_dns) {
    setPrimary(dns.primary_dns);
    setSecondary(dns.secondary_dns ?? '');
    setDomains((dns.search_domains ?? []).join(', '));
  }

  const saveMut = useMutation({
    mutationFn: () =>
      mcpCall('system-settings.system_dns', {
        action: 'set',
        primary_dns: primary,
        secondary_dns: secondary,
        search_domains: domains.split(',').map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: () => {
      toast.success(t('systemSettings.dnsSaved'));
      qc.invalidateQueries({ queryKey: ['sys-dns'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={Globe}>{t('systemSettings.dnsConfiguration')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <Label>{t('systemSettings.primaryDns')}</Label>
              <Input
                value={primary}
                onChange={(e) => setPrimary(e.target.value)}
                placeholder='1.1.1.1'
                size='sm'
              />
            </div>
            <div>
              <Label>{t('systemSettings.secondaryDns')}</Label>
              <Input
                value={secondary}
                onChange={(e) => setSecondary(e.target.value)}
                placeholder='8.8.8.8'
                size='sm'
              />
            </div>
          </div>
          <div>
            <Label>{t('systemSettings.searchDomains')}</Label>
            <Input
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              placeholder='local, company.com'
              size='sm'
            />
          </div>
          <div className='flex justify-end pt-2 border-t border-gray-800'>
            <Button
              variant='primary'
              size='sm'
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
              icon={saveMut.isPending
                ? <Loader2 size={14} className='animate-spin' />
                : <Save size={14} />}
            >
              {t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── NTP Tab ─────────────────────────────────────────────────────────────────

function NtpTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['sys-ntp'],
    queryFn: () => mcpCall('system-settings.system_ntp', { action: 'get' }),
  });

  const [servers, setServers] = useState('');
  const [enabled, setEnabled] = useState(true);

  const ntp = data as { servers?: string[]; enabled?: boolean } | undefined;
  if (ntp && !servers && ntp.servers?.length) {
    setServers(ntp.servers.join(', '));
    setEnabled(ntp.enabled ?? true);
  }

  const saveMut = useMutation({
    mutationFn: () =>
      mcpCall('system-settings.system_ntp', {
        action: 'set',
        servers: servers.split(',').map((s) => s.trim()).filter(Boolean),
        enabled,
      }),
    onSuccess: () => {
      toast.success(t('systemSettings.ntpSaved'));
      qc.invalidateQueries({ queryKey: ['sys-ntp'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Clock}>{t('systemSettings.ntpConfiguration')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        <div>
          <Label>{t('systemSettings.ntpServers')}</Label>
          <Input
            value={servers}
            onChange={(e) => setServers(e.target.value)}
            placeholder='0.pool.ntp.org, 1.pool.ntp.org'
            size='sm'
          />
        </div>
        <Toggle value={enabled} onSave={(v) => setEnabled(v)} label={t('systemSettings.ntpSync')} />
        <div className='flex justify-end pt-2 border-t border-gray-800'>
          <Button
            variant='primary'
            size='sm'
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            icon={<Save size={14} />}
          >
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Log Rotation Tab (toolbox.logrotate_get / logrotate_set) ───────────────

interface LogrotateConfig {
  app_size: string;
  app_rotate: number;
  audit_size: string;
  audit_rotate: number;
  audit_daily: boolean;
  compress: boolean;
}

interface LogrotateGetResult {
  ok: boolean;
  config: LogrotateConfig;
  status: {
    app_file_exists: boolean;
    audit_file_exists: boolean;
    app_last_rotated_at: string | null;
    audit_last_rotated_at: string | null;
    logrotate_service_failed: boolean;
  };
}

function LogrotateTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['logrotate-config'],
    queryFn: () => mcpCall<LogrotateGetResult>('toolbox.logrotate_get'),
    staleTime: 15_000,
  });

  const [appSize, setAppSize] = useState('');
  const [appRotate, setAppRotate] = useState('');
  const [auditSize, setAuditSize] = useState('');
  const [auditRotate, setAuditRotate] = useState('');
  const [auditDaily, setAuditDaily] = useState(true);
  const [compress, setCompress] = useState(true);
  const [synced, setSynced] = useState(false);

  const cfg = data?.config;
  if (cfg && !synced) {
    setAppSize(cfg.app_size);
    setAppRotate(String(cfg.app_rotate));
    setAuditSize(cfg.audit_size);
    setAuditRotate(String(cfg.audit_rotate));
    setAuditDaily(cfg.audit_daily);
    setCompress(cfg.compress);
    setSynced(true);
  }

  const saveMut = useMutation({
    mutationFn: () =>
      mcpCall('toolbox.logrotate_set', {
        app_size: appSize,
        app_rotate: Number(appRotate),
        audit_size: auditSize,
        audit_rotate: Number(auditRotate),
        audit_daily: auditDaily,
        compress,
      }),
    onSuccess: () => {
      toast.success(t('systemSettings.logrotateSaved'));
      qc.invalidateQueries({ queryKey: ['logrotate-config'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const status = data?.status;

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={Archive}>{t('systemSettings.logrotateTitle')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          {isLoading
            ? (
              <div className='flex justify-center py-8'>
                <Loader2 className='animate-spin text-gray-600' />
              </div>
            )
            : (
              <>
                <div className='flex items-center gap-4 text-xs text-gray-400 pb-2 border-b border-gray-800'>
                  <div className='flex items-center gap-1.5'>
                    <StatusDot status={status?.logrotate_service_failed ? 'error' : 'running'} />
                    <span>
                      {status?.logrotate_service_failed
                        ? t('systemSettings.logrotateServiceFailed')
                        : t('systemSettings.logrotateServiceOk')}
                    </span>
                  </div>
                  <Badge variant={status?.app_file_exists ? 'success' : 'warning'}>
                    {status?.app_file_exists
                      ? t('systemSettings.logrotateFileExists')
                      : t('systemSettings.logrotateFileMissing')}
                  </Badge>
                  <span>
                    {t('systemSettings.logrotateAppLastRotated')}:{' '}
                    {status?.app_last_rotated_at
                      ? new Date(status.app_last_rotated_at).toLocaleString()
                      : t('systemSettings.logrotateNeverRotated')}
                  </span>
                  <span>
                    {t('systemSettings.logrotateAuditLastRotated')}:{' '}
                    {status?.audit_last_rotated_at
                      ? new Date(status.audit_last_rotated_at).toLocaleString()
                      : t('systemSettings.logrotateNeverRotated')}
                  </span>
                </div>

                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <Label>{t('systemSettings.logrotateAppSize')}</Label>
                    <Input
                      value={appSize}
                      onChange={(e) => setAppSize(e.target.value)}
                      placeholder='500M'
                      size='sm'
                    />
                  </div>
                  <div>
                    <Label>{t('systemSettings.logrotateAppRotate')}</Label>
                    <Input
                      value={appRotate}
                      onChange={(e) => setAppRotate(e.target.value)}
                      placeholder='3'
                      size='sm'
                    />
                  </div>
                  <div>
                    <Label>{t('systemSettings.logrotateAuditSize')}</Label>
                    <Input
                      value={auditSize}
                      onChange={(e) => setAuditSize(e.target.value)}
                      placeholder='50M'
                      size='sm'
                    />
                  </div>
                  <div>
                    <Label>{t('systemSettings.logrotateAuditRotate')}</Label>
                    <Input
                      value={auditRotate}
                      onChange={(e) => setAuditRotate(e.target.value)}
                      placeholder='14'
                      size='sm'
                    />
                  </div>
                </div>

                <Toggle
                  value={auditDaily}
                  onSave={(v) => setAuditDaily(v)}
                  label={t('systemSettings.logrotateAuditDaily')}
                />
                <Toggle
                  value={compress}
                  onSave={(v) => setCompress(v)}
                  label={t('systemSettings.logrotateCompress')}
                />

                <div className='flex justify-end pt-2 border-t border-gray-800'>
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => saveMut.mutate()}
                    disabled={saveMut.isPending}
                    icon={saveMut.isPending
                      ? <Loader2 size={14} className='animate-spin' />
                      : <Save size={14} />}
                  >
                    {t('common.save')}
                  </Button>
                </div>
              </>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Boot Sequence Tab (toolbox.boot_status) ─────────────────────────────────
// Odezva na incident acm-dev 2026-08-05: acm-boot-sequence.sh hlídá, že Postgres
// reálně přijímá spojení PŘED startem acm-api. Tahle karta zobrazuje výsledek
// poslední sekvence + živý stav (živý stav má přednost, viz tool boot_status).

type BootPhase = {
  name: string;
  state: string;
  started_at: string | null;
  finished_at: string | null;
  duration_s: number | null;
  attempts: number;
  detail: string;
}

interface BootSequenceState {
  started_at?: string;
  updated_at?: string;
  overall?: string;
  phases?: BootPhase[];
  state?: string;
  hint?: string;
}

interface BootStatusResult {
  ok: boolean;
  overall: 'ok' | 'degraded' | 'failed';
  note?: string;
  boot_sequence: BootSequenceState;
  live: {
    services: Record<string, string>;
    db: { ok: boolean; error?: string };
    http: Record<string, number | null>;
  };
}

function overallBadgeVariant(overall: string): 'success' | 'warning' | 'error' | 'default' {
  if (overall === 'ok') return 'success';
  if (overall === 'degraded') return 'warning';
  if (overall === 'failed') return 'error';
  return 'default';
}

function phaseStatusDot(state: string): string {
  switch (state) {
    case 'ready':
      return 'running';
    case 'waiting':
    case 'pending':
      return 'pending';
    case 'failed':
      return 'error';
    case 'degraded':
      return 'pending';
    case 'skipped':
      return 'stopped';
    default:
      return state;
  }
}

function BootTab() {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['boot-status'],
    queryFn: () => mcpCall<BootStatusResult>('toolbox.boot_status'),
    staleTime: 15_000,
  });

  const seq = data?.boot_sequence;
  const phases = seq?.phases ?? [];
  const overall = data?.overall ?? 'default';

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader className='flex items-center justify-between'>
          <CardTitle icon={Power}>{t('systemSettings.bootTitle')}</CardTitle>
          <RefreshButton onRefresh={() => refetch()} loading={isFetching} />
        </CardHeader>
        <CardContent className='space-y-4'>
          {isLoading
            ? (
              <div className='flex justify-center py-8'>
                <Loader2 className='animate-spin text-gray-600' />
              </div>
            )
            : (
              <>
                <div className='flex items-center gap-4 text-xs text-gray-400 pb-2 border-b border-gray-800 flex-wrap'>
                  <div className='flex items-center gap-1.5'>
                    <span className='text-gray-500'>{t('systemSettings.bootOverall')}:</span>
                    <Badge variant={overallBadgeVariant(overall)}>{overall}</Badge>
                  </div>
                  <span>
                    {t('systemSettings.bootLastStarted')}:{' '}
                    {seq?.started_at ? new Date(seq.started_at).toLocaleString() : t('systemSettings.bootNeverRun')}
                  </span>
                  {data?.note && <span className='text-yellow-400'>{data.note}</span>}
                  {!seq?.phases && seq?.hint && <span className='text-gray-500'>{seq.hint}</span>}
                </div>

                {phases.length > 0
                  ? (
                    <DataTable
                      columns={[
                        {
                          key: 'name',
                          label: t('systemSettings.bootColPhase'),
                          render: (row: BootPhase) => row.name,
                        },
                        {
                          key: 'state',
                          label: t('systemSettings.bootColState'),
                          render: (row: BootPhase) => (
                            <StatusDot status={phaseStatusDot(row.state)} withLabel label={row.state} />
                          ),
                        },
                        {
                          key: 'duration_s',
                          label: t('systemSettings.bootColDuration'),
                          render: (row: BootPhase) => row.duration_s != null ? `${row.duration_s}s` : '—',
                        },
                        {
                          key: 'attempts',
                          label: t('systemSettings.bootColAttempts'),
                          render: (row: BootPhase) => String(row.attempts),
                        },
                        {
                          key: 'detail',
                          label: t('systemSettings.bootColDetail'),
                          render: (row: BootPhase) => (
                            <span className='text-xs text-gray-400'>{row.detail || '—'}</span>
                          ),
                        },
                      ]}
                      data={phases}
                      rowKey={(row) => row.name}
                      compact
                    />
                  )
                  : <EmptyState title={t('systemSettings.bootNoData')} />}

                <div className='pt-2 border-t border-gray-800 flex items-center gap-4 text-xs text-gray-400 flex-wrap'>
                  <span className='text-gray-500'>{t('systemSettings.bootLiveNow')}:</span>
                  {data && Object.entries(data.live.services).map(([svc, state]) => (
                    <div key={svc} className='flex items-center gap-1.5'>
                      <StatusDot status={state} />
                      <span>{svc}</span>
                    </div>
                  ))}
                  {data && (
                    <div className='flex items-center gap-1.5'>
                      <StatusDot status={data.live.db.ok ? 'running' : 'error'} />
                      <span>DB</span>
                    </div>
                  )}
                </div>
              </>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Resource Registry Tab ───────────────────────────────────────────────────

function ResourceTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [typeFilter, setTypeFilter] = useState('');

  const { data: statsData } = useQuery({
    queryKey: ['resource-stats'],
    queryFn: () => mcpCall('system-settings.resource_stats'),
    staleTime: 15_000,
  });

  const { data: listData, isLoading } = useQuery({
    queryKey: ['resource-list', typeFilter],
    queryFn: () =>
      mcpCall(
        'system-settings.resource_list',
        typeFilter ? { resource_type: typeFilter } : {},
      ),
    staleTime: 15_000,
  });

  const releaseMut = useMutation({
    mutationFn: (r: { resource_type: string; resource_id: string }) =>
      mcpCall('system-settings.resource_release', r),
    onSuccess: () => {
      toast.success(t('systemSettings.released'));
      qc.invalidateQueries({ queryKey: ['resource-list'] });
      qc.invalidateQueries({ queryKey: ['resource-stats'] });
    },
  });

  const stats = (statsData as { stats?: Record<string, { allocated: number }> })?.stats ?? {};
  const totalAlloc = (statsData as { total_allocated?: number })?.total_allocated ?? 0;
  const resources = (listData as { resources?: Array<Record<string, unknown>> })?.resources ?? [];
  const types = Object.keys(stats);

  return (
    <div className='space-y-4'>
      <StatsGrid columns={4}>
        <StatCard label={t('systemSettings.totalAllocated')} value={totalAlloc} icon={Database} />
        <StatCard label={t('systemSettings.ports')} value={stats.port?.allocated ?? 0} icon={Network} />
        <StatCard label={t('systemSettings.containers')} value={stats.container?.allocated ?? 0} icon={Server} />
        <StatCard label={t('systemSettings.types')} value={types.length} icon={Shield} />
      </StatsGrid>

      <div className='flex items-center gap-2'>
        <Select
          size='sm'
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          options={[
            { value: '', label: t('systemSettings.allTypes') },
            ...types.map((ty) => ({ value: ty, label: ty })),
          ]}
          className='w-48'
        />
      </div>

      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : resources.length === 0
        ? (
          <EmptyState
            icon={<Database size={24} />}
            title={t('systemSettings.noResources')}
            description={t('systemSettings.noResourcesDesc')}
          />
        )
        : (
          <Card>
            <CardContent>
              <div className='divide-y divide-gray-800'>
                {resources.map((r, i) => (
                  <div key={i} className='flex items-center gap-3 py-2'>
                    <Badge>{String(r.resource_type)}</Badge>
                    <span className='font-mono text-xs text-white flex-1'>
                      {String(r.resource_id)}
                    </span>
                    <span className='text-[10px] text-gray-600'>{String(r.module)}</span>
                    {!!r.instance && (
                      <span className='text-[10px] text-gray-500'>{String(r.instance)}</span>
                    )}
                    <Badge variant={r.status === 'allocated' ? 'success' : 'default'}>
                      {String(r.status)}
                    </Badge>
                    {r.status === 'allocated' && (
                      <button
                        onClick={() =>
                          releaseMut.mutate({
                            resource_type: String(r.resource_type),
                            resource_id: String(r.resource_id),
                          })}
                        className='p-1 text-gray-600 hover:text-red-400 rounded'
                        title={t('systemSettings.release')}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
}

// ── Limits Tab (platform.limits_get / platform.limits_set) ─────────────────
// Vrstvený model (Filip 2026-08-07): platforma definuje default+strop, tenant
// a user smí jen snížit (nebo zvýšit do platformního stropu). Dnes se edituje
// jen platformní vrstva — tenant/user override se zatím jen ZOBRAZUJE (ať je
// vidět, že skládání existuje), needitovatelné, dokud nepřibude UI pro výběr
// konkrétního tenanta/uživatele.

interface LimitLayerValue {
  value: number;
  set_by: string | null;
  set_at: string | null;
}

interface PlatformLimitEntry {
  default: number;
  max: number;
  set_by: string | null;
  set_at: string | null;
}

type ResolvedLimit = {
  key: string;
  description: string;
  unit: string;
  platform: PlatformLimitEntry;
  tenant: LimitLayerValue | null;
  user: LimitLayerValue | null;
  resolved: number;
  source: 'platform' | 'tenant' | 'user';
}

interface LimitsGetResult {
  ok: boolean;
  tenant_id: string | null;
  entity_id: string | null;
  limits: ResolvedLimit[];
}

function sourceBadgeVariant(source: string): 'success' | 'warning' | 'default' {
  if (source === 'user') return 'warning';
  if (source === 'tenant') return 'success';
  return 'default';
}

function LimitsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ResolvedLimit | null>(null);
  const [defaultVal, setDefaultVal] = useState('');
  const [maxVal, setMaxVal] = useState('');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['platform-limits'],
    queryFn: () => mcpCall<LimitsGetResult>('platform.limits_get'),
    staleTime: 15_000,
  });

  const saveMut = useMutation({
    mutationFn: () =>
      mcpCall('platform.limits_set', {
        key: editing!.key,
        layer: 'platform',
        default: Number(defaultVal),
        max: Number(maxVal),
      }),
    onSuccess: () => {
      toast.success(t('systemSettings.limitsSaved'));
      qc.invalidateQueries({ queryKey: ['platform-limits'] });
      setEditing(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const limits = data?.limits ?? [];

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader className='flex items-center justify-between'>
          <CardTitle icon={Gauge}>{t('systemSettings.limitsTitle')}</CardTitle>
          <RefreshButton onRefresh={() => refetch()} loading={isFetching} />
        </CardHeader>
        <CardContent className='space-y-4'>
          <p className='text-gray-500 text-xs'>{t('systemSettings.limitsHint')}</p>
          {isLoading
            ? (
              <div className='flex justify-center py-8'>
                <Loader2 className='animate-spin text-gray-600' />
              </div>
            )
            : limits.length === 0
            ? <EmptyState title={t('systemSettings.limitsNone')} />
            : (
              <DataTable
                columns={[
                  {
                    key: 'key',
                    label: t('systemSettings.limitsColKey'),
                    render: (row: ResolvedLimit) => (
                      <div>
                        <div className='font-mono text-xs text-white'>{row.key}</div>
                        <div className='text-[10px] text-gray-600'>{row.description}</div>
                      </div>
                    ),
                  },
                  {
                    key: 'default',
                    label: t('systemSettings.limitsColDefault'),
                    render: (row: ResolvedLimit) => `${row.platform.default} ${row.unit}`,
                  },
                  {
                    key: 'max',
                    label: t('systemSettings.limitsColMax'),
                    render: (row: ResolvedLimit) => `${row.platform.max} ${row.unit}`,
                  },
                  {
                    key: 'tenant',
                    label: t('systemSettings.limitsColTenant'),
                    render: (row: ResolvedLimit) =>
                      row.tenant
                        ? <span className='text-xs text-gray-300'>{row.tenant.value} {row.unit}</span>
                        : <span className='text-xs text-gray-600'>—</span>,
                  },
                  {
                    key: 'user',
                    label: t('systemSettings.limitsColUser'),
                    render: (row: ResolvedLimit) =>
                      row.user
                        ? <span className='text-xs text-gray-300'>{row.user.value} {row.unit}</span>
                        : <span className='text-xs text-gray-600'>—</span>,
                  },
                  {
                    key: 'resolved',
                    label: t('systemSettings.limitsColResolved'),
                    render: (row: ResolvedLimit) => (
                      <div className='flex items-center gap-2'>
                        <span className='text-white font-semibold text-xs'>{row.resolved} {row.unit}</span>
                        <Badge variant={sourceBadgeVariant(row.source)}>{row.source}</Badge>
                      </div>
                    ),
                  },
                  {
                    key: 'actions',
                    label: '',
                    render: (row: ResolvedLimit) => (
                      <button
                        onClick={() => {
                          setEditing(row);
                          setDefaultVal(String(row.platform.default));
                          setMaxVal(String(row.platform.max));
                        }}
                        className='px-2 py-1 text-xs text-gray-400 hover:text-white rounded border border-gray-800 hover:border-gray-700'
                      >
                        {t('common.edit')}
                      </button>
                    ),
                  },
                ]}
                data={limits}
                rowKey={(row) => row.key}
                compact
              />
            )}
        </CardContent>
      </Card>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing ? `${t('systemSettings.limitsEditTitle')} — ${editing.key}` : ''}
        size='sm'
        footer={
          <>
            <Button variant='ghost' size='sm' onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant='primary'
              size='sm'
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
              icon={saveMut.isPending ? <Loader2 size={14} className='animate-spin' /> : <Save size={14} />}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {editing && (
          <div className='space-y-3'>
            <p className='text-xs text-gray-500'>{editing.description}</p>
            <div>
              <Label>{t('systemSettings.limitsColDefault')} ({editing.unit})</Label>
              <Input value={defaultVal} onChange={(e) => setDefaultVal(e.target.value)} size='sm' />
            </div>
            <div>
              <Label>{t('systemSettings.limitsColMax')} ({editing.unit})</Label>
              <Input value={maxVal} onChange={(e) => setMaxVal(e.target.value)} size='sm' />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────

// ── AccessTab — stejné panely jako /core/acm-self (Veřejný přístup) ─────────
function AccessTab() {
  const scope = { node: null as string | null, identity: null as string | null };
  return (
    <div className='space-y-6'>
      <ServicesPanel scope={scope} />
      <AclPanel scope={scope} />
    </div>
  );
}

export default function SystemSettingsPage() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('systemSettings.pageTitle')}
        description={t('systemSettings.pageDescription')}
        icon={Settings}
      />

      <Tabs defaultTab='info'>
        <div className='space-y-5'>
          <TabList>
            <Tab id='info' icon={<Server size={14} />}>{t('systemSettings.tabInfo')}</Tab>
            <Tab id='network' icon={<Network size={14} />}>{t('systemSettings.tabNetwork')}</Tab>
            <Tab id='dns' icon={<Globe size={14} />}>{t('systemSettings.tabDns')}</Tab>
            <Tab id='ntp' icon={<Clock size={14} />}>{t('systemSettings.tabNtp')}</Tab>
            <Tab id='logrotate' icon={<Archive size={14} />}>{t('systemSettings.tabLogrotate')}</Tab>
            <Tab id='limits' icon={<Gauge size={14} />}>{t('systemSettings.tabLimits')}</Tab>
            <Tab id='boot' icon={<Power size={14} />}>{t('systemSettings.tabBoot')}</Tab>
            <Tab id='resources' icon={<Database size={14} />}>
              {t('systemSettings.tabResources')}
            </Tab>
            <Tab id='access' icon={<Globe2 size={14} />}>{t('systemSettings.tabAccess')}</Tab>
          </TabList>

          <TabPanel id='info'>
            <InfoTab />
          </TabPanel>
          <TabPanel id='network'>
            <NetworkTab />
          </TabPanel>
          <TabPanel id='dns'>
            <DnsTab />
          </TabPanel>
          <TabPanel id='ntp'>
            <NtpTab />
          </TabPanel>
          <TabPanel id='logrotate'>
            <LogrotateTab />
          </TabPanel>
          <TabPanel id='limits'>
            <LimitsTab />
          </TabPanel>
          <TabPanel id='boot'>
            <BootTab />
          </TabPanel>
          <TabPanel id='resources'>
            <ResourceTab />
          </TabPanel>
          <TabPanel id='access'>
            <AccessTab />
          </TabPanel>
        </div>
      </Tabs>
    </div>
  );
}
